import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  Alert, 
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

interface TestResult {
  endpoint: string;
  status: 'success' | 'error' | 'loading';
  data?: any;
  error?: string;
}

export const BackendIntegrationTest: React.FC = () => {
  const { user } = useAuth();
  const [results, setResults] = useState<TestResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const testEndpoints = [
    { name: 'Health Check', url: '/health' },
    { name: 'User Profile', url: '/auth/me' },
    { name: 'Analytics Dashboards', url: '/analytics/dashboards' },
    { name: 'Marketplace Suppliers', url: '/marketplace/suppliers' },
    { name: 'Teams', url: '/teams' },
    { name: 'Referrals Stats', url: '/referrals/stats' },
  ];

  const testEndpoint = async (endpoint: { name: string; url: string }) => {
    const result: TestResult = {
      endpoint: endpoint.name,
      status: 'loading'
    };

    setResults(prev => prev.map(r => r.endpoint === endpoint.name ? result : r));

    try {
      const response = await api.get(endpoint.url);
      setResults(prev => prev.map(r => 
        r.endpoint === endpoint.name 
          ? { ...result, status: 'success', data: response }
          : r
      ));
    } catch (error: any) {
      setResults(prev => prev.map(r => 
        r.endpoint === endpoint.name 
          ? { 
              ...result, 
              status: 'error', 
              error: error.response?.data?.message || error.message 
            }
          : r
      ));
    }
  };

  const testAllEndpoints = async () => {
    setIsLoading(true);
    setResults(testEndpoints.map(ep => ({ endpoint: ep.name, status: 'loading' })));

    for (const endpoint of testEndpoints) {
      await testEndpoint(endpoint);
      // Небольшая задержка между запросами
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    setIsLoading(false);
  };

  const _getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'success.main';
      case 'error': return 'error.main';
      case 'loading': return 'info.main';
      default: return 'text.secondary';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'loading': return '⏳';
      default: return '❓';
    }
  };

  return (
    <Card sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        🔗 Тест интеграции с Backend API
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Пользователь: {user?.email} (ID: {user?.id})
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Backend URL: {process.env['REACT_APP_API_BASE_URL'] || 'http://localhost:3001/api'}
        </Typography>
      </Box>

      <Button 
        variant="contained" 
        onClick={testAllEndpoints}
        disabled={isLoading}
        sx={{ mb: 2 }}
      >
        {isLoading ? <CircularProgress size={20} /> : '🧪 Тестировать все API'}
      </Button>

      <List>
        {results.map((result, index) => (
          <React.Fragment key={result.endpoint}>
            <ListItem sx={{ color: _getStatusColor(result.status) }}>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1">
                      {getStatusIcon(result.status)} {result.endpoint}
                    </Typography>
                    {result.status === 'loading' && <CircularProgress size={16} />}
                  </Box>
                }
                secondary={
                  result.status === 'error' ? (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {result.error}
                    </Alert>
                  ) : result.status === 'success' ? (
                    <Alert severity="success" sx={{ mt: 1 }}>
                      Успешно! Данные получены
                    </Alert>
                  ) : null
                }
              />
            </ListItem>
            {index < results.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </List>

      {results.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Нажмите кнопку для начала тестирования API
        </Typography>
      )}
    </Card>
  );
};

export default BackendIntegrationTest;
